import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useEmployerPartners, QUERY_KEYS } from "@/hooks/use-queries";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
  Building2,
  Users,
  DollarSign,
  CheckCircle,
  Plus,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatCard, formatCents } from "./shared";

export function EmployersDashboard() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    tier: "bronze",
    matchPercent: "",
    matchCapCents: "",
    annualBudgetCents: "",
    contributionFrequency: "monthly",
  });

  const { data: employers, isLoading } = useEmployerPartners();

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("POST", "/api/employer-partners", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employerPartners });
      toast({ title: "Employer Partner Created", description: "The employer partner has been added successfully." });
      setDialogOpen(false);
      setFormData({
        companyName: "",
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        tier: "bronze",
        matchPercent: "",
        matchCapCents: "",
        annualBudgetCents: "",
        contributionFrequency: "monthly",
      });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create employer partner.", variant: "destructive" });
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("PATCH", `/api/employer-partners/${id}`, { status: "active" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employerPartners });
      toast({ title: "Employer Approved", description: "The employer partner is now active." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to approve employer partner.", variant: "destructive" });
    },
  });

  const handleSubmit = () => {
    createMutation.mutate({
      companyName: formData.companyName,
      contactName: formData.contactName,
      contactEmail: formData.contactEmail,
      contactPhone: formData.contactPhone,
      tier: formData.tier,
      matchPercent: parseFloat(formData.matchPercent) || 0,
      matchCapCents: parseInt(formData.matchCapCents) || 0,
      annualBudgetCents: parseInt(formData.annualBudgetCents) || 0,
      contributionFrequency: formData.contributionFrequency,
    });
  };

  const totalPartners = employers?.length || 0;
  const activePartners = employers?.filter((e: any) => e.status === "active").length || 0;
  const totalEmployees = employers?.reduce((sum: number, e: any) => sum + (e.totalEmployeesEnrolled || 0), 0) || 0;
  const totalContributions = employers?.reduce((sum: number, e: any) => sum + (e.totalContributedCents || 0), 0) || 0;

  const getStatusVariant = (status: string) => {
    if (status === "active") return "default" as const;
    if (status === "pending") return "secondary" as const;
    if (status === "suspended") return "destructive" as const;
    return "secondary" as const;
  };

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="employers-loading">
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <>
      <div className="grid gap-4 md:grid-cols-4" data-testid="employers-stats">
        <StatCard
          title="Total Partners"
          value={totalPartners}
          description="All employer partners"
          icon={Building2}
          color="primary"
        />
        <StatCard
          title="Active Partners"
          value={activePartners}
          description="Currently active"
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          title="Total Employees"
          value={totalEmployees}
          description="Across all partners"
          icon={Users}
          color="blue"
        />
        <StatCard
          title="Total Contributions"
          value={formatCents(totalContributions)}
          description="All contributions"
          icon={DollarSign}
          color="purple"
        />
      </div>

      <Card data-testid="employers-table-card">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Employer Partners
          </CardTitle>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="button-add-employer">
                <Plus className="w-4 h-4 mr-2" />
                Add Employer Partner
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="dialog-add-employer">
              <DialogHeader>
                <DialogTitle data-testid="dialog-title-add-employer">Add Employer Partner</DialogTitle>
                <DialogDescription>Create a new employer partner account.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      value={formData.companyName}
                      onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                      data-testid="input-company-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Name</Label>
                    <Input
                      value={formData.contactName}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactName: e.target.value }))}
                      data-testid="input-contact-name"
                    />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                      data-testid="input-contact-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Phone</Label>
                    <Input
                      value={formData.contactPhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
                      data-testid="input-contact-phone"
                    />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Tier</Label>
                    <Select value={formData.tier} onValueChange={(val) => setFormData(prev => ({ ...prev, tier: val }))}>
                      <SelectTrigger data-testid="select-tier">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bronze">Bronze</SelectItem>
                        <SelectItem value="silver">Silver</SelectItem>
                        <SelectItem value="gold">Gold</SelectItem>
                        <SelectItem value="platinum">Platinum</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Contribution Frequency</Label>
                    <Select value={formData.contributionFrequency} onValueChange={(val) => setFormData(prev => ({ ...prev, contributionFrequency: val }))}>
                      <SelectTrigger data-testid="select-contribution-frequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="annually">Annually</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Match Percent (%)</Label>
                    <Input
                      type="number"
                      value={formData.matchPercent}
                      onChange={(e) => setFormData(prev => ({ ...prev, matchPercent: e.target.value }))}
                      data-testid="input-match-percent"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Match Cap (cents)</Label>
                    <Input
                      type="number"
                      value={formData.matchCapCents}
                      onChange={(e) => setFormData(prev => ({ ...prev, matchCapCents: e.target.value }))}
                      data-testid="input-match-cap-cents"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Annual Budget (cents)</Label>
                    <Input
                      type="number"
                      value={formData.annualBudgetCents}
                      onChange={(e) => setFormData(prev => ({ ...prev, annualBudgetCents: e.target.value }))}
                      data-testid="input-annual-budget-cents"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="button-cancel-employer">Cancel</Button>
                <Button onClick={handleSubmit} disabled={createMutation.isPending} data-testid="button-submit-employer">
                  {createMutation.isPending ? "Creating..." : "Create Partner"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto max-h-96">
            <Table data-testid="employers-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Company Name</TableHead>
                  <TableHead>Company Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Match Rate</TableHead>
                  <TableHead>Annual Budget</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employers?.map((employer: any) => (
                  <TableRow key={employer.id} data-testid={`employer-row-${employer.id}`}>
                    <TableCell className="font-medium" data-testid={`text-company-name-${employer.id}`}>{employer.companyName}</TableCell>
                    <TableCell className="font-mono text-sm" data-testid={`text-company-code-${employer.id}`}>{employer.companyCode}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(employer.status)} data-testid={`badge-status-${employer.id}`}>
                        {employer.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" data-testid={`badge-tier-${employer.id}`}>
                        {employer.tier}
                      </Badge>
                    </TableCell>
                    <TableCell data-testid={`text-match-rate-${employer.id}`}>{employer.matchPercent}%</TableCell>
                    <TableCell data-testid={`text-annual-budget-${employer.id}`}>{formatCents(employer.annualBudgetCents || 0)}</TableCell>
                    <TableCell data-testid={`text-created-${employer.id}`}>
                      {employer.createdAt ? format(new Date(employer.createdAt), "MMM d, yyyy") : "-"}
                    </TableCell>
                    <TableCell>
                      {employer.status === "pending" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => approveMutation.mutate(employer.id)}
                          disabled={approveMutation.isPending}
                          data-testid={`button-approve-${employer.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {(!employers || employers.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8" data-testid="text-no-employers">
                      No employer partners found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
